import { isNil } from 'lodash-es';
import { AUTO_POSITION, COMMENT_NODE_MIN_HEIGHT, COMMENT_NODE_MIN_WIDTH, GRID_SIZE, NODE_DIMENSION, NODE_SPACING, SUBFLOW_EXPAND_NODE_DIM } from './tokens';
import { NodeType } from './types';
/** Shared layout math only — safe to import from tests without pulling UI (styled/Button). */
/** Minimum port count on the larger side before node size scales with {@link NODE_DIMENSION_PORT_SCALING_DIVISOR}. */
const NODE_SIZE_PORT_SCALING_THRESHOLD = 4;
/** Divisor in `size * maxPorts / divisor` when scaling node size for many ports. */
const NODE_DIMENSION_PORT_SCALING_DIVISOR = 3.5;
/**
 * Scales a node dimension based on its port count. Nodes with fewer than
 * {@link NODE_SIZE_PORT_SCALING_THRESHOLD} ports on either side keep the base `size`. Once the
 * larger side reaches that many ports the dimension grows linearly:
 * `size × maxPorts / NODE_DIMENSION_PORT_SCALING_DIVISOR`, so a node with 7 ports is twice the base
 * size. This keeps ports evenly spaced without overlapping while avoiding unnecessary growth for
 * low-port nodes.
 */
function getSize(noOfSourceHandles = 0, noOfTargetHandles = 0, size) {
    const maxPorts = Math.max(noOfSourceHandles, noOfTargetHandles);
    if (maxPorts >= NODE_SIZE_PORT_SCALING_THRESHOLD) {
        size = (size * maxPorts) / NODE_DIMENSION_PORT_SCALING_DIVISOR;
    }
    return size;
}
/** Returns the square bounding box of a node, scaled from {@link NODE_DIMENSION} by port count. */
export function getNodeSize(noOfSourceHandles = 0, noOfTargetHandles = 0) {
    const size = getSize(noOfSourceHandles, noOfTargetHandles, NODE_DIMENSION);
    return { height: size, width: size };
}
/** Rounds a position to the nearest {@link GRID_SIZE} increment so nodes align to the canvas grid. Zero values are preserved as-is. */
export function snapPosition(position) {
    return {
        x: position.x ? GRID_SIZE * Math.round(position.x / GRID_SIZE) : position.x,
        y: position.y ? GRID_SIZE * Math.round(position.y / GRID_SIZE) : position.y
    };
}
/**
 * Index of an input handle on the node's WEST side (`targetHandles` order).
 * Used to align fan-in sources vertically with join port order and reduce edge crossings.
 */
export function getTargetHandleIndex(node, targetHandleId) {
    if (!targetHandleId)
        return 0;
    const idx = node.data.targetHandles?.findIndex((h) => h.id === targetHandleId) ?? -1;
    return idx >= 0 ? idx : 0;
}
/** is the x or y position is greater than AUTO_POSITION or is nil or not finite then the node is considered to be not positioned.
 */
export function isNotPositioned(position) {
    return (Math.abs(position.x) >= AUTO_POSITION ||
        Math.abs(position.y) >= AUTO_POSITION ||
        isNil(position.x) ||
        isNil(position.y) ||
        !isFinite(position.x) ||
        !isFinite(position.y));
}
export function isNodeNotPositioned(node) {
    return isNotPositioned(node.position);
}
/** checks if two rectangles overlap
 */
export function checkRectangleOverlap(rect1, rect2) {
    const left1 = rect1.x;
    const right1 = rect1.x + rect1.width;
    const top1 = rect1.y;
    const bottom1 = rect1.y + rect1.height;
    const left2 = rect2.x;
    const right2 = rect2.x + rect2.width;
    const top2 = rect2.y;
    const bottom2 = rect2.y + rect2.height;
    return left1 < right2 && right1 > left2 && top1 < bottom2 && bottom1 > top2;
}
/** Axis-aligned bounds of the rectangle spanning two canvas points (e.g. edge port positions). */
export function axisAlignedBoundsBetweenPoints(a, b) {
    return {
        minX: Math.min(a.x, b.x),
        maxX: Math.max(a.x, b.x),
        minY: Math.min(a.y, b.y),
        maxY: Math.max(a.y, b.y)
    };
}
export const sortNodesByTopLeft = (nodes) => {
    return nodes.sort((nodeA, nodeB) => {
        if (nodeA.position.y !== nodeB.position.y) {
            return nodeA.position.y - nodeB.position.y;
        }
        return nodeA.position.x - nodeB.position.x;
    });
};
/**
 * Returns the pixel width and height of a node, accounting for its type:
 * - **Subflow**: uses `data.width`/`data.height` (falling back to `node.width`/`node.height`, then
 *   {@link SUBFLOW_PROCESS_MINIMUM_DIM} — same default as subflow layout minimum size).
 * - **Comment**: uses `node.width`/`node.height` (falling back to {@link COMMENT_NODE_MIN_WIDTH} /
 *   {@link COMMENT_NODE_MIN_HEIGHT}, not {@link NODE_DIMENSION} — comment nodes use a wide, short default).
 * - **Regular node**: computed from the number of source/target ports via
 *   {@link getNodeSize}, then enlarged if the node has a custom `getSize` override.
 */
/** Map of node id → node for parent-chain canvas position resolution (React Flow nesting). */
export function buildNodeIdMap(nodes) {
    return new Map(nodes.map((n) => [n.id, n]));
}
/**
 * Top-left of the node in canvas (flow) coordinates: sums ancestor `position` along `parentNode`.
 * If a parent id is missing from `idMap`, treats the node as root for the remainder of the chain.
 */
export function getCanvasTopLeft(node, idMap) {
    if (!node.parentNode) {
        return { x: node.position.x, y: node.position.y };
    }
    const parent = idMap.get(node.parentNode);
    if (!parent) {
        return { x: node.position.x, y: node.position.y };
    }
    const parentTopLeft = getCanvasTopLeft(parent, idMap);
    return {
        x: parentTopLeft.x + node.position.x,
        y: parentTopLeft.y + node.position.y
    };
}
/** Canvas-axis-aligned bounds (same sizing rules as {@link getNodeDimensions}). */
export function getCanvasBounds(node, idMap) {
    const { x, y } = getCanvasTopLeft(node, idMap);
    const { w, h } = getNodeDimensions(node);
    return { x, y, width: w, height: h };
}
/** Collision test in canvas space (required when either node has a `parentNode`). */
export function checkNodeCollisionCanvas(node1, node2, idMap, padding = NODE_SPACING / 2) {
    const dim1 = getNodeDimensions(node1);
    const dim2 = getNodeDimensions(node2);
    const tl1 = getCanvasTopLeft(node1, idMap);
    const tl2 = getCanvasTopLeft(node2, idMap);
    const rect1 = {
        x: tl1.x - padding,
        y: tl1.y - padding,
        width: dim1.w + 2 * padding,
        height: dim1.h + 2 * padding
    };
    const rect2 = {
        x: tl2.x - padding,
        y: tl2.y - padding,
        width: dim2.w + 2 * padding,
        height: dim2.h + 2 * padding
    };
    return checkRectangleOverlap(rect1, rect2);
}
export function getNodeDimensions(node) {
    if (node.type === NodeType.DummyNode) {
        return {
            w: node.width ?? SUBFLOW_EXPAND_NODE_DIM,
            h: node.height ?? SUBFLOW_EXPAND_NODE_DIM
        };
    }
    if (node.type === NodeType.subFlow) {
        const data = node.data;
        return {
            w: data.width ?? node.width ?? SUBFLOW_EXPAND_NODE_DIM,
            h: data.height ?? node.height ?? SUBFLOW_EXPAND_NODE_DIM
        };
    }
    if (node.type === NodeType.comment) {
        return {
            w: node.width ?? COMMENT_NODE_MIN_WIDTH,
            h: node.height ?? COMMENT_NODE_MIN_HEIGHT
        };
    }
    const srcH = node.data?.sourceHandles?.length ?? 0;
    const tgtH = node.data?.targetHandles?.length ?? 0;
    const portBased = getNodeSize(srcH, tgtH);
    if (node.data?.getSize) {
        const { width, height } = node.data.getSize();
        return { w: Math.max(width, portBased.width), h: Math.max(height, portBased.height) };
    }
    return { w: portBased.width, h: portBased.height };
}
/**
 * Returns the absolute pixel position of a node's single output port — the right edge,
 * vertically centred.
 */
export function getSourcePortPosition(node) {
    const { w, h } = getNodeDimensions(node);
    return {
        x: node.position.x + w,
        y: node.position.y + h / 2
    };
}
/**
 * Returns the absolute pixel position of a specific target (input) port on the node's
 * left edge. When the node has multiple target handles, ports are evenly distributed
 * along the node height; `targetHandleId` selects which one.
 */
export function getTargetPortPosition(node, targetHandleId) {
    const { h } = getNodeDimensions(node);
    const numTargetHandles = node.data?.targetHandles?.length ?? 1;
    if (numTargetHandles <= 1 || !targetHandleId) {
        return { x: node.position.x, y: node.position.y + h / 2 };
    }
    const idx = getTargetHandleIndex(node, targetHandleId);
    const portSpacing = h / (numTargetHandles + 1);
    return {
        x: node.position.x,
        y: node.position.y + portSpacing * (idx + 1)
    };
}
